import { useCallback, useEffect, useMemo, useState } from "react";

import { SelectData } from "../../models";
import { logError } from "../../service/error";
import { lookUp, searchStandards, searchVerifiers } from "../../service/query";
import { BaseResponseWithSingle, Status } from "../../service/Shared";

type OptionsByUuid = Record<string, SelectData>;

type ProjectReferenceData = {
  standardOptions: SelectData;
  projectTypeOptions: SelectData;
  verifierOptions: SelectData;
  countryOptions: SelectData;
  standardOptionsByProjectType: OptionsByUuid;
  projectTypeOptionsByStandard: OptionsByUuid;
};

export interface ProjectReferenceOptions extends ProjectReferenceData {
  isLoading: boolean;
  hasLoadFailed: boolean;
  reload: () => void;
  getStandardOptions: (projectTypeUuid: string | null) => SelectData;
  getProjectTypeOptions: (standardUuid: string | null) => SelectData;
}

const emptyStandards = {
  standardOptions: [],
  projectTypeOptions: [],
  standardOptionsByProjectType: {},
  projectTypeOptionsByStandard: {},
};

const emptyData: ProjectReferenceData = {
  ...emptyStandards,
  verifierOptions: [],
  countryOptions: [],
};

const byDisplayName = (a: SelectData[number], b: SelectData[number]): number =>
  String(a.value ?? "").localeCompare(String(b.value ?? ""));

/*
  searchStandards and searchVerifiers are both declared [UsePaging] with no MaxPageSize, so they take
  HotChocolate's default cap of 50 - visible in the api's generated/schema.private.graphql as
  @listSize(assumedSize: 50). Asking for more than that is not clamped, it is rejected outright
  ("The maximum allowed items per page were exceeded."), which fails the whole query. So the page
  size stays inside the cap and the connection is walked instead. Walking rather than just asking
  for 50 keeps the dropdowns whole if either list ever passes a page, which a fixed size would
  silently truncate.
*/
const searchPageLimit = 50;

// A hard stop, so a server that kept reporting hasNextPage could never spin here. 20 pages is far
// beyond any real number of standards or verifiers; reaching it means something is wrong.
const maxSearchPages = 20;

interface PagedSearchResponse<T> {
  paging: {
    endCursor: string;
    hasNextPage: boolean;
  };
  results: T[];
}

const fetchAllPages = async <T>(
  search: (afterCursor: string | null, limit: number) => Promise<BaseResponseWithSingle<PagedSearchResponse<T>>>,
  failureMessage: string
): Promise<T[]> => {
  const results: T[] = [];
  let afterCursor: string | null = null;

  for (let page = 0; page < maxSearchPages; page += 1) {
    // eslint-disable-next-line no-await-in-loop
    const response = await search(afterCursor, searchPageLimit);

    // Bail rather than returning a partial list - a missing option is harder to spot than an empty
    // dropdown, and an empty dropdown reads as "your organisation has none" rather than a failure.
    // The caller turns this into a visible error.
    if (response.status !== Status.Success || !response.data?.results) {
      throw new Error(failureMessage);
    }

    results.push(...response.data.results);

    if (!response.data.paging.hasNextPage) break;
    afterCursor = response.data.paging.endCursor;
  }

  return results;
};

const fetchStandards = async (): Promise<
  Pick<
    ProjectReferenceData,
    "standardOptions" | "projectTypeOptions" | "standardOptionsByProjectType" | "projectTypeOptionsByStandard"
  >
> => {
  const standards = (
    await fetchAllPages(
      (afterCursor, limit) =>
        searchStandards({
          paging: { afterCursor, beforeCursor: null, limit },
          filter: { results: { canCreateNewProjects: { operator: "eq", value: true } } },
          sort: [{ key: "results.displayName", direction: "asc" }],
        }),
      "Could not load the standards for the project builder"
    )
  ).filter((standard) => standard.displayName);

  const standardOptions = standards.map((standard) => ({
    key: standard.uuid,
    value: standard.displayName as string,
  }));

  const projectTypeOptions = standards
    .flatMap((standard) => standard.projectTypes ?? [])
    .filter((projectType) => projectType.displayName)
    .reduce((accumulator, projectType) => {
      if (!accumulator.some((entry) => entry.key === projectType.uuid)) {
        accumulator.push({ key: projectType.uuid, value: projectType.displayName });
      }
      return accumulator;
    }, [] as SelectData)
    .sort(byDisplayName);

  const standardOptionsByProjectType = standards.reduce<OptionsByUuid>((accumulator, standard) => {
    standard.projectTypes?.forEach((projectType) => {
      if (!accumulator[projectType.uuid]) accumulator[projectType.uuid] = [];
      if (!accumulator[projectType.uuid].some((entry) => entry.key === standard.uuid)) {
        accumulator[projectType.uuid].push({ key: standard.uuid, value: standard.displayName as string });
      }
    });
    return accumulator;
  }, {});

  const projectTypeOptionsByStandard = standards.reduce<OptionsByUuid>((accumulator, standard) => {
    accumulator[standard.uuid] = (standard.projectTypes ?? [])
      .filter((projectType) => projectType.displayName)
      .map((projectType) => ({ key: projectType.uuid, value: projectType.displayName }))
      .sort(byDisplayName);
    return accumulator;
  }, {});

  return { standardOptions, projectTypeOptions, standardOptionsByProjectType, projectTypeOptionsByStandard };
};

const fetchVerifiers = async (): Promise<SelectData> => {
  const verifiers = await fetchAllPages(
    (afterCursor, limit) =>
      searchVerifiers({
        paging: { afterCursor, beforeCursor: null, limit },
        sort: [{ key: "results.displayName", direction: "asc" }],
      }),
    "Could not load the verifiers for the project builder"
  );

  // verifier.uuid is what createProject's vvbUuid expects.
  return verifiers
    .filter((verifier) => verifier.displayName)
    .map((verifier) => ({ key: verifier.uuid, value: verifier.displayName as string }));
};

const fetchCountries = async (): Promise<SelectData> => {
  const response = await lookUp({ type: "Country", sort: [{ key: "valueString", direction: "asc" }] });

  if (response.status !== Status.Success || !response.data) {
    throw new Error("Could not load the countries for the project builder");
  }

  return response.data
    .filter((country) => country.key.includes("GB-"))
    .map((country) => ({ key: country.key, value: country.valueString }));
};

interface LoadResult {
  data: ProjectReferenceData;
  hasFailed: boolean;
}

// Settled rather than all: the three are unrelated queries, so one failing should not throw away
// the other two - that would leave the create-project forms with no standard or project type to
// pick over an outage in a list one of them does not even use.
const loadProjectReferenceData = async (): Promise<LoadResult> => {
  const [standards, verifiers, countries] = await Promise.allSettled([
    fetchStandards(),
    fetchVerifiers(),
    fetchCountries(),
  ]);

  const failures = [standards, verifiers, countries].filter(
    (result): result is PromiseRejectedResult => result.status === "rejected"
  );
  failures.forEach((failure) => logError({ error: failure.reason }));

  return {
    data: {
      ...(standards.status === "fulfilled" ? standards.value : emptyStandards),
      verifierOptions: verifiers.status === "fulfilled" ? verifiers.value : [],
      countryOptions: countries.status === "fulfilled" ? countries.value : [],
    },
    hasFailed: failures.length > 0,
  };
};

// Shared across the new-project journey, the Seed variant and the deal journey's create-project
// panel, so the reference data is fetched once per session rather than per mount.
let inFlight: Promise<LoadResult> | null = null;

export const resetProjectReferenceOptionsCache = (): void => {
  inFlight = null;
};

export const useProjectReferenceOptions = (): ProjectReferenceOptions => {
  const [data, setData] = useState<ProjectReferenceData>(emptyData);
  const [isLoading, setIsLoading] = useState(true);
  const [hasLoadFailed, setHasLoadFailed] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    let isActive = true;

    if (!inFlight) inFlight = loadProjectReferenceData();

    setIsLoading(true);
    setHasLoadFailed(false);

    inFlight
      .then(({ data: result, hasFailed }) => {
        // Whatever did load is kept; only the failed slice is empty. The cached promise is dropped
        // on a failure so Try again refetches, and the page has to say so - empty dropdowns on their
        // own only produce misleading "required field" errors.
        if (hasFailed) inFlight = null;
        if (!isActive) return;
        setData(result);
        setHasLoadFailed(hasFailed);
        setIsLoading(false);
      })
      .catch((error) => {
        if (!isActive) return;
        logError({ error });
        inFlight = null;
        setData(emptyData);
        setHasLoadFailed(true);
        setIsLoading(false);
      });

    return () => {
      isActive = false;
    };
  }, [reloadKey]);

  const reload = useCallback(() => {
    inFlight = null;
    setReloadKey((previous) => previous + 1);
  }, []);

  return useMemo(
    () => ({
      ...data,
      isLoading,
      hasLoadFailed,
      reload,
      getStandardOptions: (projectTypeUuid: string | null): SelectData =>
        projectTypeUuid ? (data.standardOptionsByProjectType[projectTypeUuid] ?? []) : data.standardOptions,
      getProjectTypeOptions: (standardUuid: string | null): SelectData =>
        standardUuid ? (data.projectTypeOptionsByStandard[standardUuid] ?? []) : data.projectTypeOptions,
    }),
    [data, isLoading, hasLoadFailed, reload]
  );
};
